import React, { useRef, useEffect } from 'react';
import { Box, Paper, Typography, Chip } from '@mui/material';
import { keyframes } from '@emotion/react';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import HistoryIcon from '@mui/icons-material/History';
import ImageIcon from '@mui/icons-material/Image';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import AudioFileIcon from '@mui/icons-material/AudioFile';
import DescriptionIcon from '@mui/icons-material/Description';
import { MarkdownContent } from './MarkdownContent';
import { stripJsonBlocks } from './utils/stripJson';
import { stripCitationTokens } from '../../features/narrative-citations/parseCitations';
import type { Attachment, StreamStatus, ThreadItemData, TurnGroup } from './types';

// --- Constants ---

const flashAnimation = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.4; }
`;

// --- ThinkingToggle Component ---

interface ThinkingToggleProps {
  count: number;
  isExpanded: boolean;
  isStreaming: boolean;
  onToggle: () => void;
}

const ThinkingToggle: React.FC<ThinkingToggleProps> = ({
  count,
  isExpanded,
  isStreaming,
  onToggle
}) => {
  return (
    <Box
      onClick={onToggle}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 0.5,
        py: 1,
        px: 1.5,
        mb: 2,
        cursor: 'pointer',
        borderRadius: 1,
        bgcolor: 'action.hover',
        '&:hover': {
          bgcolor: 'action.selected',
        },
        transition: 'background-color 0.2s',
      }}
    >
      {isExpanded ? (
        <ExpandLessIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
      ) : (
        <ExpandMoreIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
      )}
      <Typography
        variant="body2"
        sx={{
          color: 'text.secondary',
          fontStyle: 'italic',
          animation: isStreaming ? `${flashAnimation} 1.2s ease-in-out infinite` : 'none',
        }}
      >
        {isStreaming ? 'Thinking...' : `Thinking (${count} messages)`}
      </Typography>
    </Box>
  );
};

// --- Attachment List Component ---

const AttachmentList: React.FC<{ attachments: Attachment[] }> = ({ attachments }) => {
  if (!attachments.length) return null;
  return (
    <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
      {attachments.map((a) => {
        const icon =
          a.kind === 'image' ? (
            <ImageIcon sx={{ fontSize: 16 }} />
          ) : a.kind === 'pdf' ? (
            <PictureAsPdfIcon sx={{ fontSize: 16 }} />
          ) : a.kind === 'document' ? (
            <DescriptionIcon sx={{ fontSize: 16 }} />
          ) : (
            <AudioFileIcon sx={{ fontSize: 16 }} />
          );

        if (a.kind === 'image' && a.previewUrl) {
          return (
            <Box
              key={a.s3Key || a.fileName}
              component="img"
              src={a.previewUrl}
              alt={a.fileName}
              sx={{
                width: 120,
                height: 120,
                borderRadius: 1,
                objectFit: 'cover',
                border: 1,
                borderColor: 'divider',
              }}
            />
          );
        }
        return (
          <Chip
            key={a.s3Key || a.fileName}
            icon={icon}
            label={a.fileName}
            size="small"
            sx={{
              maxWidth: 240,
              '& .MuiChip-label': {
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              },
            }}
          />
        );
      })}
    </Box>
  );
};

// --- Thread Item Component ---

interface ThreadItemProps {
  item: ThreadItemData;
  isCollapsible?: boolean;
}

const ThreadItem: React.FC<ThreadItemProps> = ({ item, isCollapsible = false }) => {
  const [isExpanded, setIsExpanded] = React.useState(!isCollapsible);

  if (item.content?.match(/^\n+$/)) {
    return null;
  }

  // User message
  if (item.type === 'user') {
    if (!isExpanded && isCollapsible) {
      return (
        <Box sx={{ mb: 2 }}>
          <Box
            onClick={() => setIsExpanded(true)}
            sx={{
              pl: 1.5,
              borderLeft: '3px solid #2196F3',
              borderRadius: 0.5,
              cursor: 'pointer',
              '&:hover': { opacity: 0.8 }
            }}
          >
            <Typography variant="body2" sx={{ color: 'text.secondary', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              User: {item.content}
            </Typography>
          </Box>
        </Box>
      );
    }

    return (
      <Box sx={{ mb: 3 }}>
        <Paper
          elevation={0}
          sx={{
            p: 2,
            border: 1,
            borderColor: 'divider',
            borderRadius: 3,
            boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
            bgcolor: 'background.paper',
          }}
        >
          {item.content && <MarkdownContent content={item.content} />}
          {item.attachments && item.attachments.length > 0 && (
            <AttachmentList attachments={item.attachments} />
          )}
        </Paper>
      </Box>
    );
  }

  // AI Response
  if (item.type === 'ai-response') {
    const cleanContent = stripJsonBlocks(item.content || '');
    // If the model emitted nothing but JSON, hide the bubble entirely.
    if (!cleanContent) return null;

    if (!isExpanded && isCollapsible) {
      return (
        <Box sx={{ mb: 2 }}>
          <Box
            onClick={() => setIsExpanded(true)}
            sx={{
              pl: 1.5,
              borderLeft: '3px solid #673AB7',
              borderRadius: 0.5,
              cursor: 'pointer',
              '&:hover': { opacity: 0.8 }
            }}
          >
            <Typography variant="body2" sx={{ color: 'text.secondary', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              AI: {stripCitationTokens(cleanContent)}
            </Typography>
          </Box>
        </Box>
      );
    }

    return (
      <Box sx={{ mb: 3 }}>
        <MarkdownContent content={cleanContent} />
      </Box>
    );
  }

  // Task
  if (item.type === 'task') {
    let collapsedText = 'Working...';

    return (
      <Box sx={{ mb: 2 }}>
        <Box
          onClick={() => setIsExpanded(!isExpanded)}
          sx={{
            pl: 1.5,
            borderLeft: `3px solid ${item.taskColor || '#90A4AE'}`,
            borderRadius: 0.5,
            cursor: 'pointer',
            '&:hover': { opacity: 0.8 }
          }}
        >
          <Typography
            variant="body2"
            fontWeight={500}
            sx={{
              color: item.status === 'active' ? 'primary.main' : 'text.primary',
              animation: item.status === 'active' ? `${flashAnimation} 1.2s ease-in-out infinite` : 'none',
            }}
          >
            {isExpanded ? item.title : collapsedText}
          </Typography>
        </Box>
      </Box>
    );
  }

  // Working indicator (non-task tool calls)
  if (item.type === 'working') {
    return (
      <Box sx={{ mb: 2, pl: 1.5 }}>
        <Typography
          variant="body2"
          sx={{
            color: 'text.secondary',
            animation: `${flashAnimation} 1.2s ease-in-out infinite`,
          }}
        >
          {item.title}
        </Typography>
      </Box>
    );
  }

  return null;
};

// --- Historical Turn Collapsed Component ---

interface HistoricalTurnCollapsedProps {
  group: TurnGroup;
  onExpand: () => void;
}

const HistoricalTurnCollapsed: React.FC<HistoricalTurnCollapsedProps> = ({ group, onExpand }) => {
  const rawPreview = group.firstMessage?.content || group.lastMessage?.content || '';
  const previewText = group.firstMessage?.type === 'user'
    ? rawPreview
    : stripCitationTokens(stripJsonBlocks(rawPreview));
  const truncatedPreview = previewText.length > 80
    ? previewText.substring(0, 80) + '...'
    : previewText;

  return (
    <Box
      onClick={onExpand}
      sx={{
        mb: 2,
        p: 1.5,
        borderRadius: 1,
        bgcolor: 'action.hover',
        border: 1,
        borderColor: 'divider',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        '&:hover': {
          bgcolor: 'action.selected',
        },
        transition: 'background-color 0.2s',
      }}
    >
      <ExpandMoreIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
      <Chip
        icon={<HistoryIcon sx={{ fontSize: 14 }} />}
        label="History"
        size="small"
        sx={{
          height: 20,
          fontSize: 11,
          bgcolor: 'action.selected',
          '& .MuiChip-icon': { color: 'text.secondary' },
          '& .MuiChip-label': { px: 0.75 },
        }}
      />
      <Box sx={{ flex: 1, overflow: 'hidden' }}>
        <Typography
          variant="body2"
          sx={{
            color: 'text.secondary',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {group.firstMessage?.type === 'user' ? 'User: ' : ''}
          {truncatedPreview}
        </Typography>
      </Box>
    </Box>
  );
};

// --- Turn Group Component ---

interface TurnGroupItemProps {
  group: TurnGroup;
}

const TurnGroupItem: React.FC<TurnGroupItemProps> = ({ group }) => {
  const [isExpanded, setIsExpanded] = React.useState(!group.isHistorical);
  const [isThinkingExpanded, setIsThinkingExpanded] = React.useState(false);

  // If historical and collapsed, show collapsed summary
  if (!isExpanded && group.isHistorical) {
    return (
      <HistoricalTurnCollapsed
        group={group}
        onExpand={() => setIsExpanded(true)}
      />
    );
  }

  return (
    <Box sx={group.isHistorical ? {
      borderLeft: 2,
      borderColor: 'divider',
      pl: 2,
      ml: -2,
      position: 'relative',
    } : undefined}>
      {/* History indicator for expanded historical turns */}
      {group.isHistorical && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <Chip
            icon={<HistoryIcon sx={{ fontSize: 14 }} />}
            label="History"
            size="small"
            onClick={() => setIsExpanded(false)}
            sx={{
              height: 20,
              fontSize: 11,
              bgcolor: 'action.selected',
              cursor: 'pointer',
              '& .MuiChip-icon': { color: 'text.secondary' },
              '& .MuiChip-label': { px: 0.75 },
              '&:hover': { bgcolor: 'action.focus' },
            }}
          />
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
            onClick={() => setIsExpanded(false)}
          >
            Click to collapse
          </Typography>
        </Box>
      )}

      {/* First user message - always visible and expanded */}
      {group.firstMessage && (
        <ThreadItem item={group.firstMessage} isCollapsible={false} />
      )}

      {/* Thinking section - collapsible */}
      {(group.thinkingMessages.length > 0 || group.isStreaming) && (
        <>
          <ThinkingToggle
            count={group.thinkingMessages.length}
            isExpanded={isThinkingExpanded}
            isStreaming={group.isStreaming}
            onToggle={() => setIsThinkingExpanded(!isThinkingExpanded)}
          />
          {isThinkingExpanded && group.thinkingMessages.map((item) => (
            <Box key={item.id} sx={{ pl: 2, borderLeft: 2, borderColor: 'divider' }}>
              <ThreadItem item={item} isCollapsible={true} />
            </Box>
          ))}
        </>
      )}

      {/* Final message - always visible and expanded */}
      {group.lastMessage && (
        <ThreadItem item={group.lastMessage} isCollapsible={false} />
      )}
    </Box>
  );
};

// --- Main Component ---

interface ChatMessageListProps {
  turnGroups: TurnGroup[];
  status: StreamStatus;
  isLoadingHistory?: boolean;
  /** Hint shown on an empty thread; defaults to the report-chat copy. */
  emptyStateText?: string;
}

export const ChatMessageList: React.FC<ChatMessageListProps> = ({
  turnGroups,
  status,
  isLoadingHistory = false,
  emptyStateText = 'Start a conversation with the agent',
}) => {
  const listRef = useRef<HTMLDivElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Auto-scroll
  const scrollToBottom = () => {
    if (!messagesEndRef.current) return;

    const behavior = status === 'streaming' ? 'smooth' : 'auto';
    messagesEndRef.current.scrollIntoView({ behavior, block: 'end' });
  };

  useEffect(() => {
    if (!isLoadingHistory) {
      scrollToBottom();
    }
  }, [turnGroups.length, status, isLoadingHistory]);

  return (
    <Box
      ref={listRef}
      sx={{
        flex: 1,
        overflowY: 'auto',
        px: 3,
        py: 4,
        display: 'flex',
        flexDirection: 'column',
        bgcolor: 'background.default',
      }}
    >
      <Box sx={{ maxWidth: '896px', mx: 'auto', width: '100%' }}>
        {isLoadingHistory && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <Typography sx={{ color: 'text.secondary' }}>Loading...</Typography>
          </Box>
        )}

        {turnGroups.map((group) => (
          <TurnGroupItem
            key={`turn-${group.turn}`}
            group={group}
          />
        ))}

        {/* Invisible element to scroll to */}
        <div ref={messagesEndRef} />

        {turnGroups.length === 0 && !isLoadingHistory && (
          <Box sx={{ textAlign: 'center', mt: 10, opacity: 0.5 }}>
            <Typography>{emptyStateText}</Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default ChatMessageList;
